import { useEffect, useRef, useState } from 'react';
import { api } from '@/api/client';
import { PickerGlyph } from './pickerIcons';

interface WorkflowInput {
  name: string;
  label?: string;
  placeholder?: string;
  multiline?: boolean;
}
export interface Workflow {
  id: string;
  name: string;
  description?: string;
  icon?: string;
  category?: string;
  prompt: string;
  inputs?: WorkflowInput[];
}
interface WorkflowsResponse { workflows: Workflow[] }

interface Props {
  onRun(text: string): void;
  /** True while the section is hovered — show the flyout list of workflows. */
  hovered: boolean;
}

/**
 * Workflows sidebar panel — port of legacy data-panel="workflows".
 *
 * Lists prompt recipes from /api/workflows, grouped by category. Each
 * card has Run / Edit / Delete buttons. Run renders the prompt template
 * (replacing {{input}} placeholders with user-supplied values) and
 * hands the result to the composer via props.onRun.
 */
export function WorkflowsPanel({ onRun, hovered }: Props) {
  const [rows, setRows] = useState<Workflow[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Fetch lazily — the panel renders nothing unless `hovered` (below), so
  // fetching on mount made every page boot pay for a flyout the user rarely
  // opens. Load once, on first open.
  const didFetch = useRef(false);
  useEffect(() => {
    if (!hovered || didFetch.current) return;
    didFetch.current = true;
    api.get<WorkflowsResponse>('/api/workflows')
      .then((r) => setRows(r.workflows))
      .catch((e) => setError((e as Error).message));
  }, [hovered]);

  // Drop the rendered template into the composer; any {{inputs}} stay as
  // placeholders for the user to fill in there.
  function runNow(w: Workflow): void { onRun(w.prompt); }

  // Workflows have no "active" state, so nothing pins — they all live in the
  // hover flyout. Click one to load it into the composer.
  if (!hovered) return null;

  return (
    <div className="snav__pop" role="menu">
      <div className="snav__pop-title">Run a workflow</div>
      {error && <div className="snav__pop-msg snav__pop-msg--err">{error}</div>}
      {!rows && !error && <div className="snav__pop-msg">Loading…</div>}
      {rows && rows.length === 0 && <div className="snav__pop-msg">No workflows yet.</div>}
      {(rows ?? []).map((w) => (
        <button
          key={w.id}
          type="button"
          className="snav__pop-item"
          onClick={() => runNow(w)}
          data-tip={w.name}
        >
          <PickerGlyph icon={w.icon ?? null} label={w.name} size={26} />
          <span className="snav__pop-item__name">{w.name}</span>
        </button>
      ))}
    </div>
  );
}
